import { Play } from '@tunasong/icons'
import { ChordLib, QUALITY_POOL } from '@tunasong/music-lib'
import type { QualityQuestion, QualityVerdict } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import styles from './quality-feedback.module.css'

export interface QualityFeedbackProps {
  question: QualityQuestion
  verdict: QualityVerdict
  onHearAgain: () => void
  onNext: () => void
}

/**
 * Ear Training Chord-quality ID post-answer reveal. Renders:
 *  - Verdict header ("Correct." / "Not quite.") in success/error colour.
 *  - Correct-answer sentence naming the quality + the actual chord (root
 *    surfaced post-answer for context, since the chord name is now known).
 *    Format: "That was C — a Major chord. You picked Minor."
 *  - Level-resolved lexicon body for the quality (interval-stack description).
 *  - "Hear it again" + manual "Next" actions (Bowie §Q5).
 *
 * Spec: docs/superpowers/specs/2026-05-20-ear-training-suite-design.md §4.3
 */
export const QualityFeedback: FC<QualityFeedbackProps> = props => {
  const { question, verdict, onHearAgain, onNext } = props
  const correctEntry = verdict.entry
  const pickedEntry = QUALITY_POOL.find(e => e.kind === verdict.picked) ?? correctEntry
  const lexicon = useTheoryCopy(correctEntry.lexiconId)
  const chordName = ChordLib.getName(question.chord)
  const header = verdict.correct ? 'Correct.' : 'Not quite.'
  const lexBody = lexicon?.body ?? ''
  return (
    <section className={styles.feedback} role="status" aria-live="polite">
      <p className={`${styles.header} ${verdict.correct ? styles.correct : styles.incorrect}`}>{header}</p>
      <p className={styles.body}>
        {verdict.correct ? (
          <>
            That was {chordName} — a {correctEntry.displayName} chord.
          </>
        ) : (
          <>
            That was {chordName} — a {correctEntry.displayName} chord. You picked {pickedEntry.displayName}.
          </>
        )}
      </p>
      {lexBody ? (
        <p className={styles.body}>
          <TheoryGlyphs>{lexBody}</TheoryGlyphs>
        </p>
      ) : null}
      <div className={styles.actions}>
        <Button variant="text" size="small" startIcon={<Play />} onClick={onHearAgain}>
          Hear it again
        </Button>
        <Button variant="contained" color="primary" onClick={onNext}>
          Next
        </Button>
      </div>
    </section>
  )
}
